import React from "react";
import Image from "next/image";
import { StoryblokBlockComponent } from "../../lib/storyblok/types/StoryblokComponent";
import { HeroBlock } from "../../lib/storyblok/types/blocks/HeroBlock";
import Hero from "../../ui/Hero";
import VideoPlayer from "../../ui/Common/VideoPlayer";
import Button from "../../ui/Button";
import StoryblokImage from "../StoryblokImage";
import { useLinks } from "../../lib/storyblok/useLinks";
import { ButtonStyleVariant } from "../../lib/storyblok/types/shared";
import { useContentStyle } from "../../hooks/useStyle";
import { isValidImageData, imageSizes } from "../../lib/utils/imageUtils";
import videoThumbnailFallback from "../../public/images/chapter-divider-02.png";

const HeroBlock: StoryblokBlockComponent<HeroBlock> = ({
  component,
  title,
  description,
  copy,
  label,
  backgroundImage,
  videoThumbnail,
  video,
  buttons,
  inverted,
  noOverlay,
  overlayStyle,
  overlayOpacity,
}) => {
  const resolveLink = useLinks();
  const overlayInverted = useContentStyle(overlayStyle);
  const isInverted = overlayInverted !== undefined ? overlayInverted : inverted;
  const safeOpacityValue = overlayOpacity?.value ?? 0.5;

  const buttonCollection = buttons?.map((button) => {
    let buttonType: ButtonStyleVariant = button.buttonType || "default";
    if (!component.includes("Small") && buttonType === "inverted") {
      buttonType = "default";
    }

    return (
      <Button
        key={button._uid}
        variant={buttonType ?? "default"}
        href={button.buttonLink ? resolveLink(button.buttonLink) : ""}
        size={"medium"}
      >
        {button.buttonText}
      </Button>
    );
  });

  return (
    <>
      <Hero
        title={title}
        description={description}
        copy={copy}
        label={label}
        backgroundImage={
          backgroundImage &&
          isValidImageData(backgroundImage) && (
            <StoryblokImage
              image={backgroundImage}
              layout="fill"
              objectFit="cover"
              priority
              alt={title || backgroundImage.alt || ""}
              resize={{ width: !component.includes("Small") ? 960 : undefined }}
              sizes={
                component.includes("Small")
                  ? imageSizes("100vw", {
                      desktop: "1200px",
                      xl: "1600px",
                    })
                  : imageSizes("100vw")
              }
            />
          )
        }
        video={
          video?.filename && (
            <VideoPlayer
              key="video"
              videoUrl={video?.filename}
              thumbnail={
                videoThumbnail && isValidImageData(videoThumbnail) ? (
                  <StoryblokImage
                    image={videoThumbnail}
                    layout="fill"
                    sizes={imageSizes("100vw", {
                      desktop: "1200px",
                      xl: "1600px",
                    })}
                    objectFit="cover"
                    priority
                    alt={title}
                  />
                ) : (
                  <Image
                    src={videoThumbnailFallback}
                    layout="fill"
                    objectFit="cover"
                    sizes={imageSizes("720px", {
                      desktop: "1200px",
                    })}
                  />
                )
              }
              thumbnailMode={
                component.includes("Small") ? "thumbnailOnly" : "clickable"
              }
              isActive={true}
            />
          )
        }
        small={component.includes("Small")}
        buttons={buttonCollection}
        noOverlay={noOverlay}
        inverted={isInverted}
        overlayStyle={overlayStyle}
        overlayOpacity={safeOpacityValue}
      />
    </>
  );
};

export default HeroBlock;
